import { useEffect, useRef, useState, type FormEvent } from 'react';
import { validateTastingInput } from '@muddle/core';
import { api, ApiError } from './api';

const BALANCE = [
  ['sweet', 'Sweet'],
  ['sour', 'Sour'],
  ['bitter', 'Bitter'],
  ['strong', 'Strong'],
] as const;
type Balance = Record<(typeof BALANCE)[number][0], number>;

const todayLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Quick-add tasting: rating, four balance sliders, notes. Opened from the drink page in one click. */
export function TastingDialog({
  open,
  versions,
  defaultVersionId,
  onClose,
  onSaved,
}: {
  open: boolean;
  versions: { id: string; versionNumber: number }[];
  defaultVersionId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [versionId, setVersionId] = useState(defaultVersionId);
  const [date, setDate] = useState(todayLocal());
  const [rating, setRating] = useState(0);
  const [balance, setBalance] = useState<Balance>({ sweet: 3, sour: 3, bitter: 3, strong: 3 });
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      setVersionId(defaultVersionId);
      setDate(todayLocal());
      setRating(0);
      setBalance({ sweet: 3, sour: 3, bitter: 3, strong: 3 });
      setNotes('');
      setError('');
      d.showModal();
    }
    if (!open && d.open) d.close();
  }, [open, defaultVersionId]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    // Today keeps the real time so same-day tastings stay in order; other days use local noon.
    const iso =
      date === todayLocal() ? new Date().toISOString() : new Date(`${date}T12:00:00`).toISOString();
    const v = validateTastingInput({ versionId, date: iso, rating, ...balance, notes });
    if (!v.value) {
      setError(
        v.errors.rating
          ? 'Pick a rating from 1 to 5.'
          : (Object.values(v.errors)[0] ?? 'Check the form.'),
      );
      return;
    }
    setBusy(true);
    setError('');
    try {
      await api.createTasting(v.value);
      onSaved();
    } catch (err) {
      setError(
        err instanceof ApiError || err instanceof Error ? err.message : 'Something went wrong.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <dialog
      ref={ref}
      onClose={() => {
        if (!ref.current?.open) onClose(); // ignore a stale close event after reopening
      }}
      className="mu-tasting-dialog"
    >
      <form onSubmit={(e) => void submit(e)} noValidate>
        <h2>Log a tasting</h2>
        <div className="row row-2">
          <div>
            <label htmlFor="t-version">Version</label>
            <select id="t-version" value={versionId} onChange={(e) => setVersionId(e.target.value)}>
              {versions.map((v) => (
                <option key={v.id} value={v.id}>
                  Version {v.versionNumber}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="t-date">Date</label>
            <input
              id="t-date"
              type="date"
              value={date}
              max={todayLocal()}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
        </div>

        <div className="label" id="t-rating-label">
          Rating
        </div>
        <div className="chip-row" role="radiogroup" aria-labelledby="t-rating-label">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={rating === n}
              className="chip"
              onClick={() => setRating(n)}
            >
              {n}
            </button>
          ))}
        </div>

        {BALANCE.map(([key, label]) => (
          <div key={key} className="mu-slider">
            <label htmlFor={`t-${key}`}>{label}</label>
            <input
              id={`t-${key}`}
              type="range"
              min={1}
              max={5}
              step={1}
              value={balance[key]}
              onChange={(e) => setBalance({ ...balance, [key]: Number(e.target.value) })}
            />
            <span className="num mu-slider-val">{balance[key]}</span>
          </div>
        ))}

        <div className="field">
          <label htmlFor="t-notes">Notes</label>
          <textarea
            id="t-notes"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Too sweet, needs more acid"
          />
        </div>
        {error && (
          <div className="msg err" role="alert">
            {error}
          </div>
        )}
        <div className="actions">
          <button className="btn btn-ghost" type="button" onClick={onClose}>
            Cancel
          </button>
          <span className="spacer" />
          <button className="btn btn-primary" type="submit" disabled={busy} aria-busy={busy}>
            {busy ? 'Saving…' : 'Save tasting'}
          </button>
        </div>
      </form>
    </dialog>
  );
}

/** Four tiny bars for sweet / sour / bitter / strong (1 to 5). Values are always shown as text too. */
export function BalanceBars({ sweet, sour, bitter, strong }: Balance) {
  const vals: [string, number][] = [
    ['Sweet', sweet],
    ['Sour', sour],
    ['Bitter', bitter],
    ['Strong', strong],
  ];
  return (
    <div className="mu-bal">
      {vals.map(([label, v]) => (
        <div key={label} className="mu-bal-row">
          <span>{label}</span>
          <span className="mu-bal-track" aria-hidden>
            <span style={{ width: `${(v / 5) * 100}%` }} />
          </span>
          <span className="num">{v}</span>
        </div>
      ))}
    </div>
  );
}
